import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  Tabs,
  Tab,
  Collapse,
  Chip
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SearchIcon from '@mui/icons-material/Search';
import FolderIcon from '@mui/icons-material/Folder';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ArticleIcon from '@mui/icons-material/Article';
import { useHomeState } from '../context/home-context';
import { useNavigate } from '../../../hooks/useNavigate';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`publications-tabpanel-${index}`}
      aria-labelledby={`publications-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

interface SectionPublicationsTabsProps {
  title?: string;
  subtitle?: string;
}

export default function SectionPublicationsTabs({ title, subtitle }: SectionPublicationsTabsProps = {}) {
  const { groupedDailyOfficial, categoriesWithSubcategories } = useHomeState();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [searchYear, setSearchYear] = useState('');
  const [searchCategory, setSearchCategory] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<number[]>([]);

  // Filtrar anos baseado na busca
  const filteredYears = useMemo(() => {
    if (!groupedDailyOfficial || groupedDailyOfficial.length === 0) {
      return [];
    }
    if (!searchYear.trim()) {
      return groupedDailyOfficial;
    }
    return groupedDailyOfficial.filter(item =>
      item.year?.toString().includes(searchYear.trim())
    );
  }, [groupedDailyOfficial, searchYear]);

  // Filtrar categorias baseado na busca
  const filteredCategories = useMemo(() => {
    if (!categoriesWithSubcategories || categoriesWithSubcategories.length === 0) {
      return [];
    }
    if (!searchCategory.trim()) {
      return categoriesWithSubcategories;
    }
    const searchLower = searchCategory.trim().toLowerCase();
    return categoriesWithSubcategories.filter(category => {
      const categoryMatch = category.name?.toLowerCase().includes(searchLower);
      const subcategoryMatch = category.subcategories?.some(sub =>
        sub.name?.toLowerCase().includes(searchLower)
      );
      return categoryMatch || subcategoryMatch;
    });
  }, [categoriesWithSubcategories, searchCategory]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setSearchYear('');
    setSearchCategory('');
  };

  const handleCategoryClick = (categoryId: number) => {
    setExpandedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleCategoryNavigate = (categoryId: number) => {
    navigate(`/publicacoes/${categoryId}`);
  };

  const handleSubcategoryNavigate = (categoryId: number, subcategoryId: number) => {
    navigate(`/publicacoes/${categoryId}/${subcategoryId}`);
  };

  // Se não há dados para ambas as abas, não renderiza
  if ((!groupedDailyOfficial || groupedDailyOfficial.length === 0) &&
    (!categoriesWithSubcategories || categoriesWithSubcategories.length === 0)) {
    return null;
  }

  return (
    <Box component="section" id="publicacoes-diario" sx={{ mb: 6 }}>
      <Typography
        variant="h2"
        component="h2"
        sx={{
          mb: 2,
          fontWeight: 700,
          color: 'primary.main',
          textAlign: 'left',
        }}
      >
        {title || 'Diário Oficial e Publicações'}
      </Typography>

      <Typography
        variant="body1"
        sx={{
          mb: 4,
          textAlign: 'left',
          color: 'text.secondary',
        }}
      >
        {subtitle || 'Acesse os diários oficiais do município ou consulte as publicações organizadas por categoria.'}
      </Typography>

      <Paper elevation={2} sx={{ p: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="Diário Oficial e Publicações"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            mb: 2,
          }}
        >
          <Tab
            label="Diário Oficial"
            icon={<DescriptionIcon />}
            iconPosition="start"
            disabled={!groupedDailyOfficial || groupedDailyOfficial.length === 0}
          />
          <Tab
            label="Publicações"
            icon={<ArticleIcon />}
            iconPosition="start"
            disabled={!categoriesWithSubcategories || categoriesWithSubcategories.length === 0}
          />
        </Tabs>

        {/* Aba de Diário Oficial */}
        <TabPanel value={tabValue} index={0}>
          <TextField
            fullWidth
            placeholder="Filtrar por ano (ex: 2024)"
            value={searchYear}
            onChange={(e) => setSearchYear(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            sx={{ mb: 2 }}
          />

          <List sx={{ maxHeight: 400, overflow: 'auto', mb: 2 }}>
            {filteredYears.length > 0 ? (
              filteredYears.map((item, index) => (
                <React.Fragment key={item.year}>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => navigate(`/diario-oficial/${item.year}`)}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'primary.light',
                          '& .MuiListItemIcon-root': {
                            color: 'primary.main',
                          },
                          '& .MuiListItemText-primary': {
                            color: 'primary.main',
                            fontWeight: 600,
                          },
                        },
                      }}
                    >
                      <ListItemIcon>
                        <DescriptionIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={`Diário Oficial - ${item.year}`}
                        secondary={`${item.quantity || 0} publicações`}
                      />
                      <ArrowForwardIcon sx={{ color: 'text.secondary' }} />
                    </ListItemButton>
                  </ListItem>
                  {index < filteredYears.length - 1 && <Divider />}
                </React.Fragment>
              ))
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  Nenhum ano encontrado para "{searchYear}"
                </Typography>
              </Box>
            )}
          </List>

          {filteredYears.length > 0 && (
            <>
              <Divider />
              <Box sx={{ pt: 2 }}>
                <ListItemButton
                  onClick={() => navigate('/diario-oficial')}
                  sx={{
                    backgroundColor: 'primary.main',
                    color: 'white',
                    borderRadius: 1,
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                  }}
                >
                  <ListItemText
                    primary="Ver todos os Diários Oficiais"
                    primaryTypographyProps={{
                      fontWeight: 600,
                      textAlign: 'center',
                    }}
                  />
                </ListItemButton>
              </Box>
            </>
          )}
        </TabPanel>

        {/* Aba de Publicações */}
        <TabPanel value={tabValue} index={1}>
          <TextField
            fullWidth
            placeholder="Filtrar por categoria ou subcategoria"
            value={searchCategory}
            onChange={(e) => setSearchCategory(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            sx={{ mb: 2 }}
          />

          <List sx={{ maxHeight: 400, overflow: 'auto', mb: 2 }}>
            {filteredCategories.length > 0 ? (
              filteredCategories.map((category) => (
                <React.Fragment key={category.id}>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => handleCategoryClick(category.id)}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'primary.light',
                        },
                      }}
                    >
                      <ListItemIcon>
                        <FolderIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body1" fontWeight={600}>
                              {category.name}
                            </Typography>
                            <Chip
                              label={category.publicationCount || 0}
                              size="small"
                              color="primary"
                              sx={{ minWidth: 40 }}
                            />
                          </Box>
                        }
                        secondary={
                          category.subcategories && category.subcategories.length > 0
                            ? `${category.subcategories.length} subcategorias`
                            : 'Sem subcategorias'
                        }
                      />
                      {category.subcategories && category.subcategories.length > 0 ? (
                        expandedCategories.includes(category.id) ? (
                          <ExpandLess />
                        ) : (
                          <ExpandMore />
                        )
                      ) : (
                        <ArrowForwardIcon
                          sx={{ color: 'text.secondary' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCategoryNavigate(category.id);
                          }}
                        />
                      )}
                    </ListItemButton>
                  </ListItem>

                  {/* Subcategorias */}
                  {category.subcategories && category.subcategories.length > 0 && (
                    <Collapse
                      in={expandedCategories.includes(category.id)}
                      timeout="auto"
                      unmountOnExit
                    >
                      <List component="div" disablePadding>
                        {category.subcategories.map((subcategory) => (
                          <ListItem key={subcategory.id} disablePadding>
                            <ListItemButton
                              sx={{ pl: 8 }}
                              onClick={() => handleSubcategoryNavigate(category.id, subcategory.id)}
                            >
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <ArticleIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText
                                primary={subcategory.name}
                                secondary={`${subcategory.publicationCount || 0} publicações`}
                              />
                              <ArrowForwardIcon sx={{ color: 'text.secondary' }} />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  )}

                  <Divider />
                </React.Fragment>
              ))
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  Nenhuma categoria encontrada para "{searchCategory}"
                </Typography>
              </Box>
            )}
          </List>

          {filteredCategories.length > 0 && (
            <>
              <Divider />
              <Box sx={{ pt: 2 }}>
                <ListItemButton
                  onClick={() => navigate('/publicacoes')}
                  sx={{
                    backgroundColor: 'primary.main',
                    color: 'white',
                    borderRadius: 1,
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                  }}
                >
                  <ListItemText
                    primary="Ver todas as Publicações"
                    primaryTypographyProps={{
                      fontWeight: 600,
                      textAlign: 'center',
                    }}
                  />
                </ListItemButton>
              </Box>
            </>
          )}
        </TabPanel>
      </Paper>
    </Box>
  );
}
